import { lazy, Suspense } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import './App.css';

// Layouts - eagerly loaded (critical path)
import MarketingLayout from './layouts/MarketingLayout';
import UnifiedLayout from './layouts/UnifiedLayout';

// Marketing Pages - eagerly loaded (landing pages)
import LandingPage from './pages/marketing/LandingPage';

// Auth Pages - eagerly loaded (entry points)
import LoginPage from './pages/auth/SimpleLogin';
import RegisterPage from './pages/auth/NewRegistrationPage';

// Shared Components
import ProtectedRoute from './components/shared/ProtectedRoute';
import { LoadingState } from './components/ui/LoadingState';
import NetworkStatusIndicator from './components/shared/NetworkStatusIndicator';

// Lazy loaded Auth Pages
const AdminLogin = lazy(() => import('./pages/auth/AdminLogin'));
const LinkedInCallback = lazy(() => import('./pages/auth/LinkedInCallback'));

// Lazy loaded Company Portal Pages
const CompanyDashboard = lazy(() => import('./pages/company/Dashboard'));
const CompanyProfile = lazy(() => import('./pages/company/Profile'));
const BriefWizard = lazy(() => import('./pages/company/BriefWizard'));
const MyBriefs = lazy(() => import('./pages/company/MyBriefs'));
const CXOSearch = lazy(() => import('./pages/company/CXOSearch'));
const PublicCXODiscovery = lazy(() => import('./pages/PublicCXODiscovery'));
const Shortlist = lazy(() => import('./pages/company/Shortlist'));
const Engagements = lazy(() => import('./pages/company/Engagements'));
const EngagementDetail = lazy(() => import('./pages/company/EngagementDetail'));
const CompanyMessages = lazy(() => import('./pages/company/Messages'));
const CompanyDocuments = lazy(() => import('./pages/company/Documents'));
const CompanyBilling = lazy(() => import('./pages/company/Billing'));
const UnifiedSettings = lazy(() => import('./pages/UnifiedSettings'));
const CompanySettings = lazy(() => import('./pages/company/Settings'));
const CompanyAnalyticsPage = lazy(() => import('./pages/company/AnalyticsPage'));

// Lazy loaded CXO Portal Pages
const CXODashboard = lazy(() => import('./pages/cxo/Dashboard'));
const UnifiedProfile = lazy(() => import('./pages/UnifiedProfile'));
const CXOProfile = lazy(() => import('./pages/cxo/Profile'));
const CXOServices = lazy(() => import('./pages/cxo/Services'));
const CXOAvailability = lazy(() => import('./pages/cxo/Availability'));
const CXOLeads = lazy(() => import('./pages/cxo/Leads'));
const CXOProposals = lazy(() => import('./pages/cxo/Proposals'));
const NewProposal = lazy(() => import('./pages/cxo/NewProposal'));
const CXOEngagements = lazy(() => import('./pages/cxo/Engagements'));
const CXOEngagementDetail = lazy(() => import('./pages/cxo/EngagementDetail'));
const CXOInvoicing = lazy(() => import('./pages/cxo/Invoicing'));
const CXOMessages = lazy(() => import('./pages/cxo/Messages'));
const CXOSettings = lazy(() => import('./pages/cxo/Settings'));
const CXOAnalyticsPage = lazy(() => import('./pages/cxo/AnalyticsPage'));

// Lazy loaded Insights Platform Pages
const InsightsPage = lazy(() => import('./pages/insights/InsightsPage'));
const ArticleView = lazy(() => import('./pages/insights/ArticleView'));
const MyArticles = lazy(() => import('./pages/cxo/MyArticles'));
const WriteArticle = lazy(() => import('./pages/cxo/insights/WriteArticle'));
const EditArticle = lazy(() => import('./pages/cxo/insights/EditArticle'));
const RevenueDashboard = lazy(() => import('./pages/cxo/insights/RevenueDashboard'));
const SubscriberManagement = lazy(() => import('./pages/cxo/insights/SubscriberManagement'));

// Lazy loaded Admin Portal Pages
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const VerificationQueue = lazy(() => import('./pages/admin/VerificationQueue'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminEngagements = lazy(() => import('./pages/admin/Engagements'));
const AdminDisputes = lazy(() => import('./pages/admin/Disputes'));
const AdminPayouts = lazy(() => import('./pages/admin/Payouts'));
const AdminContent = lazy(() => import('./pages/admin/Content'));
const AdminAnalytics = lazy(() => import('./pages/admin/Analytics'));
const PlatformSettings = lazy(() => import('./pages/admin/PlatformSettings'));

// Lazy loaded Member Portal Pages
const MyFeed = lazy(() => import('./pages/member/MyFeed'));
const MemberOnboarding = lazy(() => import('./pages/member/OnboardingPage'));
const MemberDashboard = lazy(() => import('./pages/member/Dashboard'));
const MemberSettings = lazy(() => import('./pages/member/Settings'));

// Lazy loaded Shared Pages
const MessagingPage = lazy(() => import('./pages/MessagingPage'));
const MeetingsPage = lazy(() => import('./pages/MeetingsPage'));
const ReviewsPage = lazy(() => import('./pages/ReviewsPage'));
const CXOProfileView = lazy(() => import('./pages/CXOProfileView'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const OfflinePage = lazy(() => import('./pages/OfflinePage'));

// Page wrapper with loading state
const PageWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingState message="Loading..." />}>
    {children}
  </Suspense>
);

function App() {
  return (
    <AuthProvider>
      <CurrencyProvider>
        <HashRouter>
        <Routes>
            {/* Root Route - Landing Page */}
            <Route path="/" element={<LandingPage />} />

            {/* Beta Routes - Full application */}
            <Route element={<MarketingLayout />}>
              <Route path="/beta" element={<LandingPage />} />
              <Route path="/beta/insights" element={<PageWrapper><InsightsPage /></PageWrapper>} />
              <Route path="/beta/insights/article/:id" element={<PageWrapper><ArticleView /></PageWrapper>} />
            </Route>

            {/* Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/admin/login" element={<PageWrapper><AdminLogin /></PageWrapper>} />
            <Route path="/auth/linkedin/callback" element={<PageWrapper><LinkedInCallback /></PageWrapper>} />

            {/* Public Routes */}
            <Route element={<MarketingLayout />}>
              <Route path="/insights" element={<PageWrapper><InsightsPage /></PageWrapper>} />
              <Route path="/insights/:slug" element={<PageWrapper><ArticleView /></PageWrapper>} />
              <Route path="/cxo/:id" element={<PageWrapper><CXOProfileView /></PageWrapper>} />
              <Route path="/search" element={<PageWrapper><SearchPage /></PageWrapper>} />
              <Route path="/discover" element={<PageWrapper><PublicCXODiscovery /></PageWrapper>} />
            </Route>

            {/* Protected Company Routes */}
            <Route path="/company" element={<ProtectedRoute allowedRoles={['company', 'admin']}><UnifiedLayout /></ProtectedRoute>}>
              <Route path="dashboard" element={<PageWrapper><CompanyDashboard /></PageWrapper>} />
              <Route path="profile" element={<PageWrapper><CompanyProfile /></PageWrapper>} />
              <Route path="briefs/new" element={<PageWrapper><BriefWizard /></PageWrapper>} />
              <Route path="my-briefs" element={<PageWrapper><MyBriefs /></PageWrapper>} />
              <Route path="cxos" element={<PageWrapper><CXOSearch /></PageWrapper>} />
              <Route path="cxo-search" element={<PageWrapper><CXOSearch /></PageWrapper>} />
              <Route path="shortlist" element={<PageWrapper><Shortlist /></PageWrapper>} />
              <Route path="engagements" element={<PageWrapper><Engagements /></PageWrapper>} />
              <Route path="engagements/:id" element={<PageWrapper><EngagementDetail /></PageWrapper>} />
              <Route path="messages" element={<PageWrapper><CompanyMessages /></PageWrapper>} />
              <Route path="documents" element={<PageWrapper><CompanyDocuments /></PageWrapper>} />
              <Route path="billing" element={<PageWrapper><CompanyBilling /></PageWrapper>} />
              <Route path="settings" element={<PageWrapper><CompanySettings /></PageWrapper>} />
              <Route path="analytics" element={<PageWrapper><CompanyAnalyticsPage /></PageWrapper>} />
            </Route>

            {/* Protected CXO Routes */}
            <Route element={<ProtectedRoute allowedRoles={['cxo', 'admin']} />}>
              <Route path="/cxo" element={<UnifiedLayout />}>
                <Route path="dashboard" element={<PageWrapper><CXODashboard /></PageWrapper>} />
              <Route path="profile" element={<PageWrapper><CXOProfile /></PageWrapper>} />
              <Route path="services" element={<PageWrapper><CXOServices /></PageWrapper>} />
              <Route path="availability" element={<PageWrapper><CXOAvailability /></PageWrapper>} />
              <Route path="leads" element={<PageWrapper><CXOLeads /></PageWrapper>} />
              <Route path="proposals/new" element={<PageWrapper><NewProposal /></PageWrapper>} />
              <Route path="proposals" element={<PageWrapper><CXOProposals /></PageWrapper>} />
              <Route path="engagements" element={<PageWrapper><CXOEngagements /></PageWrapper>} />
              <Route path="engagements/:id" element={<PageWrapper><CXOEngagementDetail /></PageWrapper>} />
              <Route path="invoicing" element={<PageWrapper><CXOInvoicing /></PageWrapper>} />
              <Route path="messages" element={<PageWrapper><CXOMessages /></PageWrapper>} />
              <Route path="settings" element={<PageWrapper><CXOSettings /></PageWrapper>} />
              <Route path="analytics" element={<PageWrapper><CXOAnalyticsPage /></PageWrapper>} />
              <Route path="articles" element={<PageWrapper><MyArticles /></PageWrapper>} />
              <Route path="articles/write" element={<PageWrapper><WriteArticle /></PageWrapper>} />
              <Route path="articles/edit/:id" element={<PageWrapper><EditArticle /></PageWrapper>} />
              <Route path="revenue" element={<PageWrapper><RevenueDashboard /></PageWrapper>} />
              <Route path="subscribers" element={<PageWrapper><SubscriberManagement /></PageWrapper>} />
              </Route>
            </Route>

            {/* Protected Member Routes */}
            <Route path="/member" element={<ProtectedRoute allowedRoles={['member', 'admin']}><UnifiedLayout /></ProtectedRoute>}>
              <Route path="dashboard" element={<PageWrapper><MemberDashboard /></PageWrapper>} />
              <Route path="onboarding" element={<PageWrapper><MemberOnboarding /></PageWrapper>} />
              <Route path="feed" element={<PageWrapper><MyFeed /></PageWrapper>} />
              <Route path="settings" element={<PageWrapper><MemberSettings /></PageWrapper>} />
            </Route>

            {/* Protected Admin Routes */}
            <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><UnifiedLayout /></ProtectedRoute>}>
              <Route path="dashboard" element={<PageWrapper><AdminDashboard /></PageWrapper>} />
              <Route path="verification" element={<PageWrapper><VerificationQueue /></PageWrapper>} />
              <Route path="users" element={<PageWrapper><AdminUsers /></PageWrapper>} />
              <Route path="engagements" element={<PageWrapper><AdminEngagements /></PageWrapper>} />
              <Route path="disputes" element={<PageWrapper><AdminDisputes /></PageWrapper>} />
              <Route path="payouts" element={<PageWrapper><AdminPayouts /></PageWrapper>} />
              <Route path="content" element={<PageWrapper><AdminContent /></PageWrapper>} />
              <Route path="analytics" element={<PageWrapper><AdminAnalytics /></PageWrapper>} />
              <Route path="settings" element={<PageWrapper><PlatformSettings /></PageWrapper>} />
            </Route>

            {/* Shared Protected Routes */}
            <Route path="/profile" element={<ProtectedRoute><UnifiedLayout /><PageWrapper><UnifiedProfile /></PageWrapper></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><UnifiedLayout /><PageWrapper><UnifiedSettings /></PageWrapper></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><UnifiedLayout /><PageWrapper><MessagingPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/meetings" element={<ProtectedRoute><UnifiedLayout /><PageWrapper><MeetingsPage /></PageWrapper></ProtectedRoute>} />
            <Route path="/reviews" element={<ProtectedRoute><UnifiedLayout /><PageWrapper><ReviewsPage /></PageWrapper></ProtectedRoute>} />

            {/* Offline Page */}
            <Route path="/offline" element={<PageWrapper><OfflinePage /></PageWrapper>} />

            {/* Catch All */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <NetworkStatusIndicator />
        </HashRouter>
      </CurrencyProvider>
    </AuthProvider>
  );
}

export default App;
// Build: Tue Mar 24 03:13:45 PM CST 2026
